/**
 * Draw parcel measurement labels (side lengths + center area/name) on an OpenLayers map.
 */
import type Map from 'ol/Map';
import Overlay from 'ol/Overlay';
import { fromLonLat } from 'ol/proj';
import { computeParcelMetrics, type LonLat } from './propertyCardGeometry';

export type ParcelLabelOverlayHandle = {
  clear: () => void;
};

function ringFromPolygonGeometry(geometry: any): LonLat[] | null {
  if (!geometry || geometry.type !== 'Polygon') return null;
  const coords = geometry.coordinates?.[0];
  if (!Array.isArray(coords) || coords.length < 3) return null;
  return coords.map((c: number[]) => [c[0], c[1]] as LonLat);
}

/**
 * Attach side + center labels for the first polygon feature in layers.
 * Returns a handle to clear overlays later.
 */
export function attachParcelLabels(
  map: Map,
  opts: {
    ring?: LonLat[];
    geometry?: { type: string; coordinates: unknown };
    name?: string;
    digipin?: string | null;
    areaLabel?: string | null;
    showSides?: boolean;
    showCenter?: boolean;
  }
): ParcelLabelOverlayHandle {
  const overlays: Overlay[] = [];

  const clear = () => {
    overlays.forEach((ov) => map.removeOverlay(ov));
    overlays.length = 0;
  };

  let ring = opts.ring || null;
  if (!ring && opts.geometry) {
    ring = ringFromPolygonGeometry(opts.geometry);
  }
  if (!ring || ring.length < 3) {
    return { clear };
  }

  // Drop closing point for compute if present
  const corners =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;

  const metrics = computeParcelMetrics(corners);
  if (!metrics) return { clear };

  const add = (lonLat: LonLat, html: string, kind: 'side' | 'center') => {
    const el = document.createElement('div');
    el.innerHTML = html;
    el.style.cssText =
      kind === 'side'
        ? [
            'background:rgba(9,21,18,0.94)',
            'color:#d9f99d',
            'font:700 11px/1.2 Inter,system-ui,sans-serif',
            'padding:3px 8px',
            'border-radius:999px',
            'border:1px solid rgba(163,230,53,0.55)',
            'white-space:nowrap',
            'pointer-events:none',
            'box-shadow:0 4px 14px rgba(0,0,0,0.45)',
            'letter-spacing:0.02em',
          ].join(';')
        : [
            'background:linear-gradient(145deg,rgba(15,25,18,0.96),rgba(11,19,43,0.96))',
            'color:#f1f5e9',
            'font:800 12px/1.35 Inter,system-ui,sans-serif',
            'padding:8px 12px',
            'border-radius:12px',
            'border:1px solid rgba(56,189,248,0.55)',
            'text-align:center',
            'pointer-events:none',
            'box-shadow:0 8px 24px rgba(0,0,0,0.5)',
            'max-width:220px',
            'backdrop-filter:blur(8px)',
          ].join(';');
    const ov = new Overlay({
      element: el,
      positioning: 'center-center',
      stopEvent: false,
      offset: kind === 'side' ? [0, 0] : [0, 0],
    });
    ov.setPosition(fromLonLat(lonLat));
    map.addOverlay(ov);
    overlays.push(ov);
  };

  if (opts.showSides !== false) {
    metrics.sides.forEach((s) => add(s.mid, s.label, 'side'));
  }

  if (opts.showCenter !== false) {
    const areaText = opts.areaLabel || metrics.areaLabel;
    const centerHtml = [
      opts.name
        ? `<div style="color:#7dd3fc;font-size:13px;margin-bottom:3px">${escapeHtml(opts.name)}</div>`
        : '',
      `<div style="color:#a3e635">${escapeHtml(areaText)}</div>`,
      opts.digipin
        ? `<div style="color:#93c5fd;font-size:11px;margin-top:3px;font-weight:700">DIGIPIN ${escapeHtml(String(opts.digipin))}</div>`
        : '',
    ].join('');
    add(metrics.centroid, centerHtml, 'center');
  }

  return { clear };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
